import {
  type AppSettingsResponse,
  appSettingsResponseSchema,
  type UpdateAppSettings,
  updateAppSettingsResponseSchema,
} from '@acasa/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { handoverAgentsKey } from '@/features/handovers/use-handovers'
import { apiFetch } from '@/lib/api'

/**
 * The handful of rules the agency may change itself — the note prefixes that
 * close a checklist step, and whether the sync mirrors archived objects.
 *
 * Both endpoints are admin-only and answer 403 otherwise, so the query is
 * gated rather than fired and caught: an agent opening Einstellungen should not
 * produce a failed request and a red card about a feature they cannot see.
 */

export const appSettingsKey = ['settings'] as const

export function useAppSettings(enabled: boolean) {
  return useQuery({
    queryKey: appSettingsKey,
    queryFn: () => apiFetch('/settings', appSettingsResponseSchema),
    enabled,
  })
}

export function useUpdateAppSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (patch: UpdateAppSettings) =>
      apiFetch('/settings', updateAppSettingsResponseSchema, { method: 'PATCH', body: patch }),
    onSuccess: (response) => {
      // The response IS the stored state, so write it rather than refetching
      // what the server just handed back. `reevaluatedItems` is dropped: it
      // describes the SAVE, not the settings, and a cache entry carrying it
      // would no longer match `appSettingsResponseSchema`.
      queryClient.setQueryData(appSettingsKey, {
        settings: response.settings,
        defaults: response.defaults,
        updatedAt: response.updatedAt,
      } satisfies AppSettingsResponse)

      // The note rule is re-applied to the local mirror inside the save, so
      // every view built on those cells is now a revision behind: the matrix,
      // an opened row's detail, and the dashboard — which counts the same cells
      // in SQL and would otherwise disagree with the list behind it.
      void queryClient.invalidateQueries({ queryKey: ['checklists'] })
      void queryClient.invalidateQueries({ queryKey: ['estate-detail'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // The Makler list the protocol editor offers is the same setting, read
      // through its own route.
      void queryClient.invalidateQueries({ queryKey: handoverAgentsKey })
    },
  })
}
